import React, { createContext, useContext, useEffect, useState } from 'react';
import { Theme } from '../types';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Force dark mode - always use dark theme
  const [theme] = useState<Theme>('dark');

  useEffect(() => {
    // Always apply dark mode to document root
    const root = document.documentElement;
    root.classList.add('dark');
    
    // Store theme preference to localStorage
    localStorage.setItem('ultron-energy-theme', 'dark');
  }, []);

  // These functions are kept for API compatibility but don't change the theme
  const toggleTheme = () => {
    // No-op: dark mode is forced
  };

  const setTheme = (_newTheme: Theme) => {
    // No-op: dark mode is forced
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
